import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';

/**
 * Special editing component for analytics parameters
 * Shows the raw value with its divisor and provides validation
 */
const AnalyticsParameterEditor = ({ 
  rowData,
  columnId, 
  originalValue, 
  onConfirm, 
  onCancel 
}) => {
  // Extract the parameter group (e.g., "P01" from "P01_0")
  const paramGroup = columnId.match(/^(P\d+)_\d+$/)[1];
  
  // Get the divisor for this parameter group
  const divisorKey = `${paramGroup}divisor`;
  const divisor = rowData[divisorKey] || 1;
  
  // Get the raw value (not the percentage)
  const rawValueKey = `raw_${columnId}`;
  const rawValue = rowData[rawValueKey] !== undefined ? rowData[rawValueKey] : 0;
  
  // State for the current value being edited
  const [value, setValue] = useState(rawValue);
  
  // Handle input change
  const handleChange = (e) => {
    // Allow only numbers and empty string (for backspace)
    const newValue = e.target.value === '' ? '' : parseFloat(e.target.value);
    if (!isNaN(newValue) || e.target.value === '') {
      setValue(e.target.value === '' ? '' : newValue);
    }
  };
  
  // Validate total values for this parameter group
  const validateTotal = useCallback(() => {
    // Get all parameters from the same group (e.g., P01_0, P01_1, etc.)
    const paramIndexes = [];
    
    // Figure out how many parameters are in this group
    // Check which raw_P{group}_{index} values exist in rowData
    for (let i = 0; i < 10; i++) { // Assuming max 10 params per group
      const testKey = `raw_${paramGroup}_${i}`;
      if (rowData[testKey] !== undefined) {
        paramIndexes.push(i);
      }
    }
    
    // Sum up all values except the current one being edited
    let totalOthers = 0;
    paramIndexes.forEach(index => {
      const paramKey = `raw_${paramGroup}_${index}`;
      // Skip the current parameter being edited
      if (paramKey !== rawValueKey) {
        totalOthers += (rowData[paramKey] || 0);
      }
    });
    
    // Add the current value being edited
    const numericValue = parseFloat(value) || 0;
    const newTotal = totalOthers + numericValue;
    
    // Check if the total exceeds the divisor
    if (newTotal > divisor) {
      toast.error(`The total value (${newTotal}) exceeds the divisor (${divisor}) for parameter group ${paramGroup}`);
      return false;
    }
    
    // Optionally warn if the total is less than the divisor
    if (newTotal < divisor) {
      toast.warning(`The total value (${newTotal}) is less than the divisor (${divisor}) for parameter group ${paramGroup}`);
      // Still return true as this is just a warning
    }
    
    return true;
  }, [value, rowData, paramGroup, divisor, rawValueKey]);
  
  // Handle form submission
  const handleSubmit = () => {
    if (validateTotal()) {
      onConfirm(parseFloat(value) || 0);
    }
  };
  
  return (
    <div className="flex items-center w-full">
      <input
        type="text"
        value={value}
        onChange={handleChange}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleSubmit();
          } else if (e.key === 'Escape') {
            onCancel();
          }
        }}
        className="w-1/2 border-r-0 rounded-l text-center p-1"
        autoFocus
      />
      <div className="w-1/2 bg-gray-100 border border-l-0 rounded-r text-center p-1 text-gray-600">
        / {divisor}
      </div>
    </div>
  );
};

export default AnalyticsParameterEditor;